import { Space, Button, Typography, theme, Dropdown } from "antd";
import {
  LikeOutlined,
  LikeFilled,
  MessageOutlined,
  EyeOutlined,
  ShareAltOutlined,
  StarOutlined,
  StarFilled,
  MoreOutlined,
  InfoCircleOutlined,
  EditOutlined,
  TagOutlined,
  LinkOutlined,
  CheckOutlined,
} from "@ant-design/icons";
import { AnnotatedDeepLink, DigestV2State } from "pages/api/deep-links/types";
import { CustomTooltip } from "src/components/CustomTooltip";
import { useLinkPermissions } from "src/hooks/useLinkPermissions";
import { useRouter } from "next/router";
import { Tags } from ".";
import { useApplicationContext } from "src/hooks/useApplication";
import { RecordsAnalyzedTag } from "./RecordsAnalyzedTag";
import { FavoriteButton } from "src/components/FavoriteButton";
import { CopyButton } from "src/components/CopyButton";
import { ItemType, TopicItem } from "src/components/useLibrary";
import {
  serializeParams,
  ensureState,
} from "src/search/lib/params-state-reducer";
import { useMemo } from "react";

interface CardControlsProps {
  className?: string;
  onShare?: () => void;
  onFollow?: () => void;
  link: AnnotatedDeepLink<DigestV2State>;
  hideFavorite?: boolean;
  hideCopyLink?: boolean;
  hideView?: boolean;
  hideEdit?: boolean;
  processedRecords?: number;
  totalRecords?: number;
  tags: string[];
  footerOptions?: {
    showTags?: boolean;
    showProcessedRecords?: boolean;
    enableShare?: boolean;
  };
}

export function CardControls({
  className,
  link,
  onShare,
  onFollow,
  hideFavorite = false,
  hideCopyLink = false,
  hideEdit = false,
  processedRecords,
  totalRecords,
  tags,
  footerOptions = {
    showTags: true,
    showProcessedRecords: true,
    enableShare: false,
  },
}: CardControlsProps) {
  const { token } = theme.useToken();

  const showTags = footerOptions.showTags;

  const topicItem = useMemo(
    () => ({ ...link, itemType: ItemType.TOPICS } as TopicItem),
    [link]
  );

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        marginTop: "auto",
        width: "100%",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
        }}
      >
        {showTags && (
          <>
            <div style={{ width: "100%" }}>
              <Tags tags={tags} />
            </div>
          </>
        )}
        <Space>
          {hideFavorite === false && link && (
            <FavoriteButton item={topicItem} />
          )}
          {hideCopyLink === false &&
            link &&
            link.id &&
            (() => {
              const orgId = link.orgId;
              const topicId = link.id;
              const params = (link.json as any)?.params || {};
              const serialized = serializeParams(ensureState(params));
              const topicUrl =
                typeof window === "undefined"
                  ? ""
                  : `${window.location.origin}/${orgId}/topics/${topicId}?s=${serialized}`;
              return (
                <CopyButton
                  text={topicUrl}
                  icon={<LinkOutlined />}
                  clickedIcon={<CheckOutlined />}
                  type="text"
                  label={null}
                  tooltip={"Copy link to this card"}
                />
              );
            })()}
          {hideEdit === false && (
            <ManageCardControls
              link={link}
              processedRecords={processedRecords}
              totalRecords={totalRecords}
            />
          )}
        </Space>
      </div>
    </div>
  );
}

interface ManageCardControlsProps {
  link: AnnotatedDeepLink<DigestV2State & { topicCard?: { name?: string } }>;
  className?: string;
  processedRecords?: number;
  totalRecords?: number;
}

function ManageCardControls({
  link,
  className,
  processedRecords,
  totalRecords,
}: ManageCardControlsProps) {
  const router = useRouter();
  const { orgId } = router.query;
  const { canEdit } = useLinkPermissions(link);
  const { token } = theme.useToken();
  const { isSuperAdmin } = useApplicationContext();

  if (canEdit === false) return null;

  const items = [
    {
      key: "edit",
      label: "Edit Topic",
      icon: <EditOutlined />,
      onClick: () => {
        router.push(`/${orgId}/topics/${link.id}/edit`);
      },
    },
  ];

  const tooltipContent = (
    <>
      <div>
        <strong>Name:</strong> {link.name}
      </div>
      <div>
        <strong>Description:</strong> {link.description || "N/A"}
      </div>
      <div>
        <strong>Type:</strong> {link.json.topicCard?.name || "N/A"}
      </div>
    </>
  );

  return (
    <Space>
      <CustomTooltip content={tooltipContent} placement="top" size="large">
        <InfoCircleOutlined
          style={{
            color: token.colorTextSecondary,
            cursor: "help",
          }}
        />
      </CustomTooltip>
      {isSuperAdmin && (
        <Dropdown menu={{ items }} trigger={["click"]} placement="bottomRight">
          <Button
            type="text"
            icon={<MoreOutlined />}
            className="card-control-button"
            onClick={(e) => e.stopPropagation()}
          />
        </Dropdown>
      )}
    </Space>
  );
}
